import { createRouter, type RouterHistory } from '@tanstack/react-router';
import { createQueryClient } from './lib/query/client.js';
import { authenticatedRoute } from './routes/authenticated.js';
import { dashboardIndexRoute } from './routes/dashboard-placeholder.js';
import { forgotPasswordRoute } from './routes/forgot-password.js';
import { loginRoute } from './routes/login.js';
import { resetPasswordRoute } from './routes/reset-password.js';
import { rootRoute } from './routes/root.js';
import { ordersRoute } from './routes/commandes.js';
import { orderDetailRoute } from './routes/commande-detail.js';
import { invoiceDetailRoute } from './routes/commande-facture-detail.js';
import { filesRoute } from './routes/fichiers.js';
import { bundlesRoute } from './routes/bundles.js';
import { bundleDetailRoute } from './routes/bundle-detail.js';
import { bundleNewRoute } from './routes/bundle-new.js';
import { transporteursRoute } from './routes/transporteurs.js';
import { transporteurNewRoute } from './routes/transporteur-new.js';
import { transporteurDetailRoute } from './routes/transporteur-detail.js';
import { operationsRoute } from './routes/operations.js';
import { operationDetailRoute } from './routes/operation-detail.js';
import { operationNewRoute } from './routes/operation-new.js';
import { abonnementsRoute } from './routes/abonnements.js';
import { journalRoute } from './routes/journal.js';
import { journalErrorLinesRoute } from './routes/journal-lignes-en-erreur.js';
import { journalRunDetailRoute } from './routes/journal-run-detail.js';
import { parametresRoute } from './routes/parametres.js';
import { utilisateursRoute } from './routes/utilisateurs.js';

/**
 * Point unique d'assemblage de l'arbre de routes — voir `README-shell.md`
 * pour la convention (un fichier par route sous `src/routes/`, ce fichier ne
 * fait qu'importer et composer).
 *
 * `createAppRouter` existe pour que les tests (garde d'authentification,
 * formulaire de connexion) montent le **vrai** arbre de routes avec un
 * historique mémoire et un `QueryClient` isolés, plutôt qu'une reconstruction
 * approximative sujette à diverger de la production.
 */
const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authenticatedRoute.addChildren([
    dashboardIndexRoute,
    ordersRoute,
    // Avant `/commandes/$id` : « factures » n'est pas un id de commande.
    invoiceDetailRoute,
    orderDetailRoute,
    filesRoute,
    bundlesRoute,
    // Avant `/bundles/$sku` : « nouveau » n'est pas un SKU.
    bundleNewRoute,
    bundleDetailRoute,
    transporteursRoute,
    transporteurNewRoute,
    transporteurDetailRoute,
    operationsRoute,
    // Avant `/operations/$id` : « nouvelle » n'est pas un identifiant.
    operationNewRoute,
    operationDetailRoute,
    abonnementsRoute,
    journalRoute,
    // Avant `/journal/runs/$type/$id` : « lignes-en-erreur » n'est pas un type de run.
    journalErrorLinesRoute,
    journalRunDetailRoute,
    parametresRoute,
    utilisateursRoute,
  ]),
]);

export function createAppRouter(options: { history?: RouterHistory } = {}) {
  const queryClient = createQueryClient();
  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    ...(options.history ? { history: options.history } : {}),
  });
  return { router, queryClient };
}

const defaultInstance = createAppRouter();
export const router = defaultInstance.router;
export const queryClient = defaultInstance.queryClient;

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
